import { Badge } from '@/components/ui/badge'
import { remainingPercent } from '@/frontend/utils/tokenUsage'
import { formatPercentage } from '../../utils'

export interface DailyUsage {
  hasPlan: boolean
  percentUsed?: number | null
  status?: 'ok' | 'warning' | 'critical'
}

interface Props {
  dailyUsage: DailyUsage
}

function progressTone(percent: number) {
  if (percent >= 100) return 'bg-red-500'
  if (percent >= 90) return 'bg-orange-500'
  if (percent >= 80) return 'bg-yellow-500'
  return 'bg-green-500'
}

function badgeTone(percent: number): 'success' | 'warning' | 'critical' {
  if (percent >= 100) return 'critical'
  if (percent >= 80) return 'warning'
  return 'success'
}

export default function DailyUsagePanel({ dailyUsage }: Props) {
  if (!dailyUsage.hasPlan) {
    return (
      <div className="mb-6 rounded-xl border border-dashed border-border bg-muted/30 p-4">
        <p className="text-sm font-medium text-foreground">Uso diário normalizado</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Worker sem limite diário configurado.
        </p>
      </div>
    )
  }

  const percent = typeof dailyUsage.percentUsed === 'number' ? dailyUsage.percentUsed : 0
  const clampedPercent = Math.min(percent, 100)
  const availablePercent = remainingPercent(percent)

  return (
    <div className="mb-6 rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-foreground">Uso diário normalizado</p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Consumo acumulado de hoje para este digital worker.
          </p>
        </div>
        {availablePercent !== null && (
          <Badge variant={badgeTone(percent)}>{formatPercentage(availablePercent)}% disponível hoje</Badge>
        )}
      </div>

      <div className="mt-4">
        <p className="text-sm text-foreground">
          <span className="font-semibold">{formatPercentage(percent)}%</span>
          <span className="text-muted-foreground"> do limite diário utilizado</span>
        </p>
        <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-accent">
          <div
            className={`h-full rounded-full transition-all ${progressTone(percent)}`}
            style={{ width: `${clampedPercent}%` }}
          />
        </div>
      </div>
    </div>
  )
}
